import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { toastApiError } from "lib/toastApiError";
import { addTransactionToGroup, confirmSettlement, rejectSettlement } from "features/groups/api/Group";
import { buildSettlementTransaction } from "utils/settlement";
import { useTranslation } from "shared/hooks/useTranslation";
import { useUserSession } from "shared/contexts/UserContext";
import type { GroupSummary } from "types/Group";
import type { PairwiseDebt } from "types/Balance";
import type { GroupTransaction } from "types/GroupTransaction";
import type { Group } from "./useGroupDetails";
import { dedupePeopleById, participantDisplayName } from "features/groups/lib/groupPeople";

interface UseGroupActionsParams {
    selectedGroup: Group;
    setSelectedGroup: (g: Group) => void;
    currentLanguage: string;
}

function mergeGroupResponse(prev: Group, updated: Group): Group {
    return {
        ...prev,
        ...updated,
        _id: updated._id ?? prev._id,
        name: updated.name ?? prev.name,
        inviteToken: updated.inviteToken ?? prev.inviteToken,
        people: updated.people ?? prev.people,
        transactions: updated.transactions ?? prev.transactions,
    };
}

export function useGroupActions({ selectedGroup, setSelectedGroup, currentLanguage }: UseGroupActionsParams) {
    const { t } = useTranslation();
    const { profile } = useUserSession();
    const navigate = useNavigate();
    const [settlingDebtKey, setSettlingDebtKey] = useState<string | undefined>();
    const [confirmingSettlementId, setConfirmingSettlementId] = useState<string | undefined>();
    const [rejectingSettlementId, setRejectingSettlementId] = useState<string | undefined>();

    const debtKey = (d: PairwiseDebt) => `${d.debtor}|${d.creditor}|${d.currency}|${d.amount}`;

    const creditorDisplayName = useCallback((creditorId: string) => {
        const people = dedupePeopleById(selectedGroup?.people ?? []);
        const person = people.find((p) => p.id === creditorId);
        return person ? participantDisplayName(person) : t("common.unknownParticipant");
    }, [selectedGroup?.people, t]);

    const handleUpdateGroup = useCallback(
        (updated: Partial<GroupSummary>) => setSelectedGroup({ ...selectedGroup, ...updated }),
        [selectedGroup, setSelectedGroup],
    );

    const handleRemoveMember = useCallback(
        (id: string) => {
            toast.info(
                t("groups.memberWillBeRemoved", {
                    defaultValue: `Member ${id} will be removed from the group`,
                    id,
                }),
            );
            setSelectedGroup({
                ...selectedGroup,
                people: selectedGroup.people.map((p) => p.id === id ? { ...p, active: false } : p),
            });
        },
        [selectedGroup, setSelectedGroup, t],
    );

    const handleDeleteGroup = useCallback(() => {
        toast.info(
            t("groups.groupWillBeDeletedRedirect", {
                defaultValue: "Group will be deleted and you will be redirected",
            }),
        );
        navigate(`/${profile.username}/groups`);
    }, [profile.username, navigate, t]);

    const handleSettleDebt = useCallback(
        async (debt: PairwiseDebt) => {
            if (!selectedGroup?._id || !selectedGroup?.people?.length) return;
            const key = debtKey(debt);
            setSettlingDebtKey(key);
            try {
                const settlementTx = buildSettlementTransaction(
                    debt,
                    t("balances.debtPaymentName", { defaultValue: "Pago de deuda" }),
                );
                const updatedGroup = await addTransactionToGroup(selectedGroup._id, settlementTx, currentLanguage);
                setSelectedGroup(mergeGroupResponse(selectedGroup, updatedGroup as Group));
                toast.success(
                    t("balances.paymentRegisteredWaiting", {
                        name: creditorDisplayName(debt.creditor),
                    }),
                );
            } catch (err) {
                console.error("Failed to settle debt:", err);
                toastApiError(err, t, "balances.debtSettleError");
                throw err;
            } finally {
                setSettlingDebtKey(undefined);
            }
        },
        [selectedGroup, setSelectedGroup, currentLanguage, t, creditorDisplayName],
    );

    const handleConfirmSettlement = useCallback(
        async (tx: GroupTransaction, destinationPoolId?: string) => {
            if (!selectedGroup?._id || !tx._id) return;
            setConfirmingSettlementId(tx._id);
            try {
                const updatedGroup = await confirmSettlement(selectedGroup._id, tx._id, destinationPoolId);
                setSelectedGroup(mergeGroupResponse(selectedGroup, updatedGroup as Group));
                toast.success(t("balances.receiptConfirmed"));
            } catch (err) {
                console.error("Failed to confirm settlement:", err);
                toastApiError(err, t, "balances.confirmReceiptError");
                throw err;
            } finally {
                setConfirmingSettlementId(undefined);
            }
        },
        [selectedGroup, setSelectedGroup, t],
    );

    const handleRejectSettlement = useCallback(
        async (tx: GroupTransaction) => {
            if (!selectedGroup?._id || !tx._id) return;
            setRejectingSettlementId(tx._id);
            try {
                const updatedGroup = await rejectSettlement(selectedGroup._id, tx._id);
                setSelectedGroup(mergeGroupResponse(selectedGroup, updatedGroup as Group));
                toast.success(t("balances.paymentRejected"));
            } catch (err) {
                console.error("Failed to reject settlement:", err);
                toastApiError(err, t, "balances.rejectPaymentError");
                throw err;
            } finally {
                setRejectingSettlementId(undefined);
            }
        },
        [selectedGroup, setSelectedGroup, t],
    );

    return {
        handleUpdateGroup,
        handleRemoveMember,
        handleDeleteGroup,
        handleSettleDebt,
        handleConfirmSettlement,
        handleRejectSettlement,
        settlingDebtKey,
        confirmingSettlementId,
        rejectingSettlementId,
    };
}
